import {
  Button,
  Icons,
  notification,
  Row,
  Space,
  Spin,
  Table,
  Typography,
} from '@pankod/refine-antd';
import { useParams } from '@pankod/refine-react-router-v6';
import { useEffect, useState } from 'react';
import {
  createProductRecipe,
  getMaterialVariantsDropdown,
  getProductVariantRecipes,
  IMaterialVariantDropdown,
} from 'services/product';
import { AddMaterialDrawer } from './AddMaterialDrawer';

export type MaterialVariantType = Omit<IMaterialVariantDropdown, 'sellingPrice'> & {
  quantity: number;
  notes: string | undefined;
};

export const ProductBomTab = () => {
  const [openAddMaterial, setOpenAddMaterial] = useState<boolean>(false);
  const [materialOptions, setMaterialOptions] = useState<IMaterialVariantDropdown[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<MaterialVariantType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { id: productId } = useParams();

  useEffect(() => {
    (async () => {
      if (!productId) return;

      try {
        setIsLoading(true);
        const [variants, recipes] = await Promise.all([
          getMaterialVariantsDropdown(),
          getProductVariantRecipes(productId),
        ]);

        setMaterialOptions(variants);
        setSelectedMaterials(
          recipes.map((r) => ({
            id: r.usedVariantId,
            name: r.usedVariantName,
            quantity: r.usedProductQuantity,
            purchasePrice: r.usedProductUnitPrice,
            notes: r.note ?? undefined,
          }))
        );
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [productId]);

  const showAddMaterialDrawer = () => {
    setOpenAddMaterial(true);
  };

  const closeAddMaterialDrawer = () => {
    setOpenAddMaterial(false);
  };

  const handleAddMaterialDrawerSave = (formValues: MaterialVariantType) => {
    setSelectedMaterials((prev) => [...prev, { ...formValues }]);
    closeAddMaterialDrawer();
  };

  const handleRemoveMaterial = (id: string) => {
    const updatedMaterialVariants = selectedMaterials.filter((material) => material.id !== id);
    setSelectedMaterials(updatedMaterialVariants);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await createProductRecipe(
        productId!,
        selectedMaterials.map((v) => ({
          usedVariantId: v.id,
          usedProductQuantity: v.quantity,
          materialCost: v.purchasePrice * v.quantity,
          notes: v.notes ?? null,
        }))
      );
      notification.success({
        message: 'Success',
        description: 'Recipe saved successfully',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const totalCost = selectedMaterials.reduce((acc, curr) => {
    return acc + curr.purchasePrice * curr.quantity;
  }, 0);

  return (
    <>
      {isLoading ? (
        <Spin
          spinning
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
          }}
        />
      ) : (
        <Row>
          <AddMaterialDrawer
            open={openAddMaterial}
            materialOptions={materialOptions}
            selectedMaterials={selectedMaterials}
            onClose={closeAddMaterialDrawer}
            onSave={handleAddMaterialDrawerSave}
          />

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.5rem',
              padding: '0 0.3rem',
              marginTop: '1rem',
            }}
          >
            <Space direction="vertical">
              <Typography.Text strong>Ingredients / Materials</Typography.Text>
              <Typography.Text>per 1 pcs of product</Typography.Text>
            </Space>

            <Space>
              <Button onClick={showAddMaterialDrawer}>Add Material</Button>
            </Space>
          </div>

          <Table
            dataSource={selectedMaterials}
            rowKey="id"
            scroll={{ x: true }}
            pagination={false}
            bordered
            style={{ width: '100%' }}
            footer={() => (
              <div style={{ textAlign: 'right', fontWeight: 'bold', paddingRight: '16px' }}>
                Total Cost: {(totalCost).toFixed(2)}
              </div>
            )}
          >
            <Table.Column dataIndex="name" title="Product" />
            <Table.Column dataIndex="quantity" title="Quantity" />
            <Table.Column dataIndex="notes" title="Notes" />
            <Table.Column
              dataIndex="cost"
              title="Ingredients / Material Cost"
              render={(_, record: MaterialVariantType) => {
                return (record.purchasePrice * record.quantity).toFixed(2);
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              width={120}
              render={(_, record: MaterialVariantType): React.ReactNode => {
                return (
                  <Space>
                    <Button
                      icon={<Icons.DeleteOutlined />}
                      danger
                      type="text"
                      onClick={() => handleRemoveMaterial(record.id)}
                    />
                  </Space>
                );
              }}
            />
          </Table>

          <Button
            type="primary"
            loading={isSaving}
            disabled={isSaving}
            onClick={handleSave}
            style={{ marginLeft: 'auto', marginTop: '1rem' }}
          >
            Save
          </Button>
        </Row>
      )}
    </>
  );
};
